/**
 * FieldRenderer - 场线渲染器
 * @description 使用 Phaser Graphics API 绘制电场线和磁感线
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import { EMField } from './EMField.ts';
import type { ChargeSource, MagneticSource } from './EMField.ts';

/** 默认渲染配置 */
const DEFAULT_RENDER_CONFIG = {
  /** 电场线颜色（正电荷出发） */
  electricFieldColor: 0xff6b6b,
  /** 电场线颜色（负电荷吸引） */
  electricFieldColorNegative: 0x4ecdc4,
  /** 磁感线颜色 */
  magneticFieldColor: 0x9b59b6,
  /** 场线宽度 */
  lineWidth: 2,
  /** 场线密度（每个电荷源的场线数量） */
  fieldLineCount: 12,
  /** 场线起始半径 */
  startRadius: 30,
  /** 场线步进大小 */
  stepSize: 10,
  /** 场线最大长度 */
  maxLineLength: 400,
  /** 场线箭头间隔 */
  arrowInterval: 50,
  /** 箭头大小 */
  arrowSize: 8,
};

/** 渲染配置接口 */
export interface FieldRenderConfig {
  electricFieldColor?: number;
  electricFieldColorNegative?: number;
  magneticFieldColor?: number;
  lineWidth?: number;
  fieldLineCount?: number;
  startRadius?: number;
  stepSize?: number;
  maxLineLength?: number;
  arrowInterval?: number;
  arrowSize?: number;
}

/** 电荷源渲染标记 */
export interface ChargeSourceMarker {
  id: string;
  graphics: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
}

/** 磁场源渲染标记 */
export interface MagneticSourceMarker {
  id: string;
  graphics: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
}

/**
 * FieldRenderer 类
 * 负责将电磁场可视化绘制到 Phaser 场景
 */
export class FieldRenderer {
  private scene: Phaser.Scene;
  private emField: EMField;
  private graphics: Phaser.GameObjects.Graphics;
  private config: typeof DEFAULT_RENDER_CONFIG;
  private offsetY: number;
  private scale: number;
  private chargeMarkers: Map<string, ChargeSourceMarker> = new Map();
  private magneticMarkers: Map<string, MagneticSourceMarker> = new Map();
  private particleGraphics: Map<string, Phaser.GameObjects.Graphics> = new Map();

  /**
   * 创建场线渲染器
   * @param scene Phaser 场景实例
   * @param emField 电磁场实例
   * @param config 渲染配置（可选）
   * @param offsetY y 轴偏移（用于坐标系转换），默认 600
   * @param scale 渲染缩放因子，默认 1
   */
  constructor(
    scene: Phaser.Scene,
    emField: EMField,
    config?: FieldRenderConfig,
    offsetY: number = 600,
    scale: number = 1
  ) {
    this.scene = scene;
    this.emField = emField;
    this.config = { ...DEFAULT_RENDER_CONFIG, ...config };
    this.offsetY = offsetY;
    this.scale = scale;

    this.graphics = scene.add.graphics();
    this.graphics.setDepth(5);
  }

  /**
   * 渲染所有场线
   * 包括电场线、磁感线、电荷源标记、磁场源标记
   */
  render(): void {
    this.graphics.clear();
    this.renderElectricFieldLines();
    this.renderMagneticFieldLines();
    this.renderChargeSourceMarkers();
    this.renderMagneticSourceMarkers();
  }

  /**
   * 渲染电场线
   * 从正电荷出发，沿场方向绘制
   */
  renderElectricFieldLines(): void {
    const chargeSources = this.emField.getAllChargeSources();

    for (const source of chargeSources) {
      const color = source.charge > 0
        ? this.config.electricFieldColor
        : this.config.electricFieldColorNegative;

      const startPoints = this.emField.getElectricFieldLineStartPoints(
        source.id,
        this.config.fieldLineCount,
        this.config.startRadius
      );

      for (const startPoint of startPoints) {
        this.drawSingleElectricFieldLine(startPoint, color);
      }
    }
  }

  /**
   * 绘制单条电场线
   * 沿场方向追踪并绘制
   */
  private drawSingleElectricFieldLine(startPoint: Vec2, color: number): void {
    this.graphics.lineStyle(this.config.lineWidth, color, 0.8);

    const screenStart = this.toScreenCoords(startPoint);
    this.graphics.beginPath();
    this.graphics.moveTo(screenStart.x, screenStart.y);

    let currentPoint = startPoint;
    let lineLength = 0;
    let lastArrowDistance = 0;

    while (lineLength < this.config.maxLineLength) {
      currentPoint = this.emField.traceElectricFieldLineStep(currentPoint, this.config.stepSize);
      lineLength += this.config.stepSize;

      // 检查是否接近负电荷（终点）
      const nearNegativeCharge = this.isNearNegativeCharge(currentPoint);
      if (nearNegativeCharge) {
        const screenPoint = this.toScreenCoords(currentPoint);
        this.graphics.lineTo(screenPoint.x, screenPoint.y);
        break;
      }

      // 检查是否离开屏幕边界
      if (this.isOutOfBounds(currentPoint)) {
        break;
      }

      const screenPoint = this.toScreenCoords(currentPoint);
      this.graphics.lineTo(screenPoint.x, screenPoint.y);

      // 添加方向箭头
      if (lineLength - lastArrowDistance >= this.config.arrowInterval) {
        this.drawArrowAt(currentPoint, color);
        lastArrowDistance = lineLength;
      }
    }

    this.graphics.strokePath();
  }

  /**
   * 检查是否接近负电荷
   */
  private isNearNegativeCharge(point: Vec2): boolean {
    const chargeSources = this.emField.getAllChargeSources();
    for (const source of chargeSources) {
      if (source.charge < 0) {
        const distance = Math.sqrt(
          Math.pow(point.x - source.position.x, 2) +
          Math.pow(point.y - source.position.y, 2)
        );
        if (distance < this.config.startRadius * 1.5) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * 检查是否超出边界
   */
  private isOutOfBounds(point: Vec2): boolean {
    const screenPoint = this.toScreenCoords(point);
    return (
      screenPoint.x < 0 ||
      screenPoint.x > 800 ||
      screenPoint.y < 0 ||
      screenPoint.y > 600
    );
  }

  /**
   * 绘制方向箭头
   */
  private drawArrowAt(point: Vec2, color: number): void {
    const field = this.emField.calculateElectricField(point);
    if (field.magnitude === 0) return;

    const screenPoint = this.toScreenCoords(point);
    const angle = Math.atan2(field.y, field.x);

    // 翻转角度（因为 y 轴翻转）
    const screenAngle = -angle + Math.PI;

    this.graphics.save();
    this.graphics.translateCanvas(screenPoint.x, screenPoint.y);
    this.graphics.rotateCanvas(screenAngle);

    this.graphics.fillStyle(color, 0.8);
    this.graphics.beginPath();
    this.graphics.moveTo(this.config.arrowSize, 0);
    this.graphics.lineTo(-this.config.arrowSize / 2, -this.config.arrowSize / 2);
    this.graphics.lineTo(-this.config.arrowSize / 2, this.config.arrowSize / 2);
    this.graphics.closePath();
    this.graphics.fillPath();

    this.graphics.restore();
  }

  /**
   * 渲染磁感线
   * 简化模型：绘制同心圆表示磁场方向
   */
  renderMagneticFieldLines(): void {
    const magneticSources = this.emField.getAllMagneticSources();

    for (const source of magneticSources) {
      this.drawMagneticFieldLinesForSource(source);
    }
  }

  /**
   * 绘制单个磁场源的磁感线
   */
  private drawMagneticFieldLinesForSource(source: MagneticSource): void {
    const screenCenter = this.toScreenCoords(source.position);
    const radius = source.radius ?? 100;

    this.graphics.lineStyle(this.config.lineWidth, this.config.magneticFieldColor, 0.6);

    // 绘制同心圆（表示磁场强度分布）
    const circleCount = 4;
    for (let i = 1; i <= circleCount; i++) {
      const circleRadius = (radius / circleCount) * i;
      this.graphics.strokeCircle(screenCenter.x, screenCenter.y, circleRadius * this.scale);
    }

    // 绘制 N/S 极方向标记
    if (source.strength > 0) {
      // N 极：向外发散
      this.drawMagneticPoleMarkers(screenCenter, radius, true);
    } else {
      // S 极：向内汇聚
      this.drawMagneticPoleMarkers(screenCenter, radius, false);
    }

    // 绘制磁场方向箭头（表示磁场旋转方向）
    this.drawMagneticDirectionArrows(screenCenter, radius, source.strength > 0);
  }

  /**
   * 绘制磁极标记
   */
  private drawMagneticPoleMarkers(center: { x: number; y: number }, _radius: number, isNorth: boolean): void {
    const markerRadius = 15;
    const markerColor = isNorth ? 0xe74c3c : 0x3498db;
    const label = isNorth ? 'N' : 'S';

    this.graphics.fillStyle(markerColor, 1);
    this.graphics.fillCircle(center.x, center.y, markerRadius);

    // 添加标签
    const existingMarker = this.magneticMarkers.get(`pole_${center.x}_${center.y}`);
    if (!existingMarker) {
      const labelObj = this.scene.add.text(center.x, center.y, label, {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      labelObj.setDepth(6);
    }
  }

  /**
   * 绘制磁场方向箭头
   * 表示磁感线的旋转方向
   */
  private drawMagneticDirectionArrows(center: { x: number; y: number }, radius: number, isNorth: boolean): void {
    const arrowRadius = radius * 0.7;
    const arrowCount = 8;
    const angleStep = (2 * Math.PI) / arrowCount;

    // 磁场方向：N 极逆时针，S 极顺时针（根据右手定则）
    const rotationDirection = isNorth ? 1 : -1;

    this.graphics.fillStyle(this.config.magneticFieldColor, 0.8);

    for (let i = 0; i < arrowCount; i++) {
      const angle = i * angleStep;
      const arrowX = center.x + arrowRadius * Math.cos(angle);
      const arrowY = center.y + arrowRadius * Math.sin(angle);

      // 箭头指向旋转方向
      const arrowAngle = angle + rotationDirection * Math.PI / 2;

      this.graphics.save();
      this.graphics.translateCanvas(arrowX, arrowY);
      this.graphics.rotateCanvas(arrowAngle);

      this.graphics.beginPath();
      this.graphics.moveTo(this.config.arrowSize, 0);
      this.graphics.lineTo(-this.config.arrowSize / 2, -this.config.arrowSize / 2);
      this.graphics.lineTo(-this.config.arrowSize / 2, this.config.arrowSize / 2);
      this.graphics.closePath();
      this.graphics.fillPath();

      this.graphics.restore();
    }
  }

  /**
   * 渲染电荷源标记
   * 绘制电荷位置和电量标注
   */
  renderChargeSourceMarkers(): void {
    const chargeSources = this.emField.getAllChargeSources();

    // 移除不再存在的标记
    for (const [id] of this.chargeMarkers) {
      if (!chargeSources.find(s => s.id === id)) {
        const marker = this.chargeMarkers.get(id);
        if (marker) {
          marker.graphics.destroy();
          marker.label.destroy();
        }
        this.chargeMarkers.delete(id);
      }
    }

    // 创建或更新标记
    for (const source of chargeSources) {
      this.createOrUpdateChargeMarker(source);
    }
  }

  /**
   * 创建或更新电荷源标记
   */
  private createOrUpdateChargeMarker(source: ChargeSource): void {
    const screenPos = this.toScreenCoords(source.position);
    const markerRadius = 20;
    const color = source.charge > 0 ? 0xe74c3c : 0x3498db;
    const label = source.charge > 0 ? '+' : '-';

    let marker = this.chargeMarkers.get(source.id);

    if (!marker) {
      const markerGraphics = this.scene.add.graphics();
      markerGraphics.setDepth(6);

      const labelObj = this.scene.add.text(screenPos.x, screenPos.y, label, {
        fontSize: '18px',
        color: '#ffffff',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      labelObj.setDepth(7);

      marker = {
        id: source.id,
        graphics: markerGraphics,
        label: labelObj,
      };
      this.chargeMarkers.set(source.id, marker);
    }

    // 更新位置
    marker.graphics.clear();
    marker.graphics.fillStyle(color, 1);
    marker.graphics.fillCircle(screenPos.x, screenPos.y, markerRadius);
    marker.graphics.lineStyle(2, 0xffffff, 1);
    marker.graphics.strokeCircle(screenPos.x, screenPos.y, markerRadius);
    marker.label.setPosition(screenPos.x, screenPos.y);
  }

  /**
   * 渲染磁场源标记
   */
  renderMagneticSourceMarkers(): void {
    const magneticSources = this.emField.getAllMagneticSources();

    for (const [id] of this.magneticMarkers) {
      if (!magneticSources.find(s => s.id === id)) {
        const marker = this.magneticMarkers.get(id);
        if (marker && marker.id !== `pole_${marker.graphics.x}_${marker.graphics.y}`) {
          marker.graphics.destroy();
          marker.label.destroy();
        }
        this.magneticMarkers.delete(id);
      }
    }

    for (const source of magneticSources) {
      this.createOrUpdateMagneticMarker(source);
    }
  }

  /**
   * 创建或更新磁场源标记
   */
  private createOrUpdateMagneticMarker(source: MagneticSource): void {
    const screenPos = this.toScreenCoords(source.position);
    const markerRadius = 25;
    const label = source.strength > 0 ? 'N' : 'S';
    const color = source.strength > 0 ? 0xe74c3c : 0x3498db;

    let marker = this.magneticMarkers.get(source.id);

    if (!marker) {
      const markerGraphics = this.scene.add.graphics();
      markerGraphics.setDepth(6);

      const labelObj = this.scene.add.text(screenPos.x, screenPos.y, label, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      labelObj.setDepth(7);

      marker = {
        id: source.id,
        graphics: markerGraphics,
        label: labelObj,
      };
      this.magneticMarkers.set(source.id, marker);
    }

    marker.graphics.clear();
    marker.graphics.fillStyle(color, 0.9);
    marker.graphics.fillCircle(screenPos.x, screenPos.y, markerRadius);
    marker.graphics.lineStyle(3, 0xffffff, 1);
    marker.graphics.strokeCircle(screenPos.x, screenPos.y, markerRadius);
    marker.label.setPosition(screenPos.x, screenPos.y);

    // 添加磁场强度数值标注
    const strengthLabel = this.scene.add.text(
      screenPos.x,
      screenPos.y + markerRadius + 15,
      `B=${Math.abs(source.strength).toFixed(1)}`,
      {
        fontSize: '12px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);
    strengthLabel.setDepth(7);
  }

  /**
   * 渲染带电粒子
   * @param particleId 粒子标识
   * @param position 粒子位置
   * @param charge 粒子电荷（用于颜色）
   */
  renderParticle(particleId: string, position: Vec2, charge: number): void {
    let particleGraphic = this.particleGraphics.get(particleId);

    if (!particleGraphic) {
      particleGraphic = this.scene.add.graphics();
      particleGraphic.setDepth(8);
      this.particleGraphics.set(particleId, particleGraphic);
    }

    const screenPos = this.toScreenCoords(position);
    const color = charge > 0 ? 0xff6b6b : 0x4ecdc4;
    const radius = 10;

    particleGraphic.clear();
    particleGraphic.fillStyle(color, 1);
    particleGraphic.fillCircle(screenPos.x, screenPos.y, radius);
    particleGraphic.lineStyle(2, 0xffffff, 1);
    particleGraphic.strokeCircle(screenPos.x, screenPos.y, radius);
  }

  /**
   * 移除粒子渲染
   */
  removeParticleRender(particleId: string): void {
    const particleGraphic = this.particleGraphics.get(particleId);
    if (particleGraphic) {
      particleGraphic.destroy();
      this.particleGraphics.delete(particleId);
    }
  }

  /**
   * 转换物理坐标到屏幕坐标
   * y 轴翻转：物理 y 向上 -> 屏幕 y 向下
   */
  private toScreenCoords(pos: Vec2): { x: number; y: number } {
    return {
      x: pos.x * this.scale,
      y: this.offsetY - pos.y * this.scale,
    };
  }

  /**
   * 更新渲染配置
   */
  updateConfig(config: FieldRenderConfig): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * 获取当前配置
   */
  getConfig(): typeof DEFAULT_RENDER_CONFIG {
    return { ...this.config };
  }

  /**
   * 清除所有渲染
   */
  clear(): void {
    this.graphics.clear();

    for (const marker of this.chargeMarkers.values()) {
      marker.graphics.destroy();
      marker.label.destroy();
    }
    this.chargeMarkers.clear();

    for (const marker of this.magneticMarkers.values()) {
      marker.graphics.destroy();
      marker.label.destroy();
    }
    this.magneticMarkers.clear();

    for (const graphic of this.particleGraphics.values()) {
      graphic.destroy();
    }
    this.particleGraphics.clear();
  }

  /**
   * 销毁渲染器
   */
  destroy(): void {
    this.clear();
    this.graphics.destroy();
  }

  /**
   * 获取 Graphics 对象
   */
  getGraphics(): Phaser.GameObjects.Graphics {
    return this.graphics;
  }
}